import { Dialog, DialogContent, DialogActions, Chip } from "@mui/material";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";

import {
  FaUser,
  FaClock,
  FaHistory,
  FaCheck,
  FaTools,
  FaCalendarAlt,
  FaDollarSign,
  FaMapMarkerAlt,
  FaPen,
} from "react-icons/fa";

import dayjs from "utils/dayjs";
import useCommon from "hooks/useCommon";
import OverlayLoader from "components/OverlayLoader";
import { useState } from "react";
import { updateMaintenanceTaskStatus } from "store/slice/maintenanceSlice";
import usePermissions from "hooks/usePermissions";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

const { black, dark, primary, success, error, warning, light, info } = colors;
const { borderWidth } = borders;

const priorityColors = {
  Low: "#94a3b8",
  Medium: primary.main,
  High: warning.main,
  Urgent: error.main,
};

const sectionStyles = {
  background: light.main,
  border: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
  boxShadow: "none",
};

const subtleSectionStyles = {
  background: light.main,
  border: `${borderWidth[1]} solid ${rgba(black.main, 0.07)}`,
};

const iconColor = rgba(dark.main, 0.65);

const InfoRow = ({ icon, label, value }) => (
  <VuiBox display="flex" alignItems="center" gap={1}>
    <VuiBox
      width={26}
      height={26}
      minWidth={26}
      borderRadius="9px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        background: light.secondary,
        color: iconColor,
        border: `${borderWidth[1]} solid ${rgba(black.main, 0.06)}`,
      }}
    >
      {icon}
    </VuiBox>

    <VuiBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
      <VuiTypography variant="caption" color="dark" sx={{ opacity: 0.7 }}>
        {label}:
      </VuiTypography>

      <VuiTypography variant="caption" color="dark" fontWeight="bold">
        {value || "-"}
      </VuiTypography>
    </VuiBox>
  </VuiBox>
);

const MaintenanceDetailDialog = ({
  open,
  onClose,
  task,
  loading = false,
  error: taskError = null,
  onEdit,
}) => {
  const { DISPATCH, STORE } = useCommon();
  const { can } = usePermissions();

  const { updateTaskStatusLoading, updateTaskStatusError } = STORE((state) => state.maintenance);

  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);

  const logs = task?.activityLogs || [];

  const assignedStaff = Array.isArray(task?.assignedStaff)
    ? task.assignedStaff
        .map((staff) => staff?.name || staff?.fullName || staff?.email)
        .filter(Boolean)
        .join(", ")
    : task?.assignedStaff?.name ||
      task?.assignedStaff?.fullName ||
      task?.assignedStaff ||
      "Unassigned";

  const isCompleted = task?.status === "completed" || task?.status === "Completed";

  const priorityColor = priorityColors[task?.priority] || primary.main;

  const handleConfirmComplete = async () => {
    try {
      await DISPATCH(
        updateMaintenanceTaskStatus({
          taskId: task.id,
          status: "Completed",
        })
      ).unwrap();

      setConfirmCompleteOpen(false);
      onClose();
    } catch (err) {}
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <OverlayLoader open={loading} message="Loading task details..." />

          {taskError && (
            <VuiBox
              p={1.6}
              mb={2}
              borderRadius="14px"
              sx={{
                background: light.secondary,
                border: `${borderWidth[1]} solid ${rgba(error.main, 0.22)}`,
                borderLeft: `4px solid ${error.main}`,
              }}
            >
              <VuiTypography variant="caption" color="dark" fontWeight="medium">
                {typeof taskError === "string"
                  ? taskError
                  : taskError?.message || "Failed to load maintenance task"}
              </VuiTypography>
            </VuiBox>
          )}

          <VuiBox display="flex" flexDirection="column" gap={2}>
            {/* Header */}
            <VuiBox p={2} borderRadius="16px" sx={sectionStyles}>
              <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                <VuiBox>
                  <VuiTypography variant="h5" color="dark" fontWeight="bold">
                    {task?.taskTitle || "Maintenance Task"}
                  </VuiTypography>

                  <VuiTypography variant="caption" color="dark" sx={{ opacity: 0.7 }}>
                    {task?.roomArea || "No area provided"}
                  </VuiTypography>
                </VuiBox>

                {task?.priority && (
                  <Chip
                    size="small"
                    label={task.priority}
                    sx={{
                      background: rgba(priorityColor, 0.1),
                      color: priorityColor,
                      border: `${borderWidth[1]} solid ${rgba(priorityColor, 0.28)}`,
                      fontWeight: 700,
                    }}
                  />
                )}
              </VuiBox>

              <VuiBox mt={1.5} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <VuiBox display="flex" alignItems="center" gap={0.6}>
                  <FaUser size={12} color={iconColor} />

                  <VuiTypography variant="caption" color="dark" sx={{ opacity: 0.78 }}>
                    Created by {task?.createdBy?.name || "System"}
                  </VuiTypography>
                </VuiBox>

                <VuiBox display="flex" alignItems="center" gap={0.6}>
                  <FaClock size={12} color={iconColor} />

                  <VuiTypography variant="caption" color="dark" sx={{ opacity: 0.78 }}>
                    {task?.createdAt
                      ? dayjs.tz(task.createdAt).format("MMM D, YYYY • h:mm A")
                      : "-"}
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
            </VuiBox>

            {/* Description */}
            <VuiBox p={2} borderRadius="16px" sx={subtleSectionStyles}>
              <VuiTypography variant="button" color="dark" fontWeight="bold" display="block">
                Description
              </VuiTypography>

              <VuiTypography
                variant="caption"
                color="dark"
                display="block"
                mt={1}
                sx={{
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                  opacity: 0.82,
                }}
              >
                {task?.description || "No description provided."}
              </VuiTypography>
            </VuiBox>

            {/* Task Info */}
            <VuiBox
              display="grid"
              gridTemplateColumns={{
                xs: "1fr",
                md: "1fr 1fr",
              }}
              borderRadius="16px"
              sx={subtleSectionStyles}
              p={2}
              gap={1.6}
            >
              <InfoRow icon={<FaTools size={12} />} label="Category" value={task?.category} />

              <InfoRow icon={<FaMapMarkerAlt size={12} />} label="Area" value={task?.roomArea} />

              <InfoRow
                icon={<FaCalendarAlt size={12} />}
                label="Scheduled"
                value={task?.scheduledDate ? dayjs(task.scheduledDate).format("MMM DD, YYYY") : "-"}
              />

              <InfoRow
                icon={<FaDollarSign size={12} />}
                label="Estimated Cost"
                value={`$${Number(task?.estimatedRepairCost || 0).toLocaleString()}`}
              />
            </VuiBox>

            {/* Assigned Staff */}
            <VuiBox>
              <VuiTypography variant="caption" color="dark" fontWeight="bold" mb={1}>
                Assigned Staff
              </VuiTypography>

              {assignedStaff !== "Unassigned" ? (
                <VuiBox
                  px={1.4}
                  py={0.7}
                  borderRadius="12px"
                  display="inline-flex"
                  alignItems="center"
                  gap={0.7}
                  sx={subtleSectionStyles}
                >
                  <FaUser size={10} color={iconColor} />

                  <VuiTypography variant="caption" color="dark" fontWeight="medium">
                    {assignedStaff}
                  </VuiTypography>
                </VuiBox>
              ) : (
                <VuiTypography variant="caption" color="dark" sx={{ opacity: 0.7 }}>
                  Unassigned
                </VuiTypography>
              )}
            </VuiBox>

            {/* Activity */}
            <VuiBox mt={1}>
              <VuiBox display="flex" alignItems="center" gap={1} mb={1}>
                <FaHistory size={14} color={iconColor} />

                <VuiTypography variant="button" color="dark" fontWeight="bold">
                  Activity
                </VuiTypography>
              </VuiBox>

              <VuiBox
                px={2}
                py={2}
                borderRadius="16px"
                sx={{
                  ...subtleSectionStyles,
                  minHeight: "120px",
                  maxHeight: "320px",
                  overflowY: "auto",
                }}
              >
                {logs.length === 0 ? (
                  <VuiTypography variant="caption" color="dark" sx={{ opacity: 0.7 }}>
                    No activity yet
                  </VuiTypography>
                ) : (
                  <VuiBox display="flex" flexDirection="column" gap={1}>
                    {logs.map((log, index) => (
                      <VuiBox
                        key={index}
                        pb={1.5}
                        sx={{
                          borderBottom: `${borderWidth[1]} solid ${rgba(black.main, 0.07)}`,
                          "&:last-child": {
                            borderBottom: "none",
                            pb: 0,
                          },
                        }}
                      >
                        <VuiBox display="flex" alignItems="center" gap={0.8}>
                          <VuiBox
                            width="8px"
                            height="8px"
                            borderRadius="50%"
                            sx={{
                              background: info.basic,
                            }}
                          />

                          <VuiTypography variant="caption" color="dark" fontWeight="bold">
                            {log.message}
                          </VuiTypography>
                        </VuiBox>

                        <VuiTypography
                          variant="caption"
                          color="dark"
                          sx={{
                            display: "block",
                            mt: 0.4,
                            fontSize: "11px",
                            opacity: 0.68,
                            pl: 2,
                          }}
                        >
                          {log.actorName || log.actor?.name || "System"}
                        </VuiTypography>

                        <VuiTypography
                          variant="caption"
                          color="dark"
                          sx={{
                            display: "block",
                            fontSize: "11px",
                            opacity: 0.68,
                            pl: 2,
                          }}
                        >
                          {dayjs(log.createdAt).format("MMM DD, YYYY • h:mm A")}
                        </VuiTypography>
                      </VuiBox>
                    ))}
                  </VuiBox>
                )}
              </VuiBox>
            </VuiBox>
          </VuiBox>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <VuiBox display="flex" justifyContent="flex-end" gap={1} width="100%">
            <VuiButton variant="outlined" color="primary" onClick={onClose}>
              Close
            </VuiButton>

            {can("editMaintenance") && (
              <VuiButton
                variant="contained"
                color="primary"
                startIcon={<FaPen size={11} />}
                onClick={() => onEdit?.(task)}
              >
                Edit Task
              </VuiButton>
            )}

            {can("editMaintenance") && (
              <VuiButton
                variant="contained"
                color="success"
                startIcon={!isCompleted && <FaCheck size={11} />}
                disabled={isCompleted}
                onClick={() => setConfirmCompleteOpen(true)}
              >
                {isCompleted ? "Completed" : "Mark As Done"}
              </VuiButton>
            )}
          </VuiBox>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmCompleteOpen}
        onClose={(_, reason) => {
          if (updateTaskStatusLoading) return;
          if (reason === "backdropClick") return;
          setConfirmCompleteOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <VuiBox display="flex" flexDirection="column" alignItems="center" textAlign="center">
            <VuiBox
              width={34}
              height={34}
              borderRadius="50%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mb={2}
              sx={{
                background: rgba(success.main, 0.12),
                border: `${borderWidth[1]} solid ${rgba(success.main, 0.28)}`,
                color: success.main,
              }}
            >
              <FaCheck size={13} />
            </VuiBox>

            <VuiTypography
              variant="body2"
              color="dark"
              fontWeight="medium"
              sx={{
                fontSize: "14px",
                lineHeight: 1.6,
              }}
            >
              Are you sure you want to mark this maintenance task as completed?
            </VuiTypography>

            {updateTaskStatusError && (
              <VuiTypography mt={2} variant="caption" color="error">
                {updateTaskStatusError}
              </VuiTypography>
            )}
          </VuiBox>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <VuiButton
            variant="outlined"
            color="primary"
            disabled={updateTaskStatusLoading}
            onClick={() => setConfirmCompleteOpen(false)}
          >
            Cancel
          </VuiButton>

          <VuiButton
            color="success"
            variant="contained"
            disabled={updateTaskStatusLoading}
            onClick={handleConfirmComplete}
          >
            {updateTaskStatusLoading ? "Updating..." : "Mark Completed"}
          </VuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MaintenanceDetailDialog;
